import { Injectable } from '@angular/core';
import { HashMap } from '@ngneat/transloco';
import {
  TokenizeResult,
  TranslationMarkupRenderer,
  TranslationMarkupRendererFactory,
  TranslationMarkupTranspiler,
  TranslationMarkupTranspilerContext,
  TranspileResult
} from 'ngx-transloco-markup';

export class SpanStart {
  constructor(readonly styles: string) {}
}

export const SPAN_END = new (class SpanEnd {})();

@Injectable()
export class SpanTranspiler implements TranslationMarkupTranspiler {
  constructor(private readonly rendererFactory: TranslationMarkupRendererFactory) {}

  tokenize(translation: string, offset: number): TokenizeResult | undefined {
    return this.recognizeSpanStartToken(translation, offset) || this.recognizeSpanEndToken(translation, offset);
  }

  private recognizeSpanStartToken(translation: string, offset: number): TokenizeResult | undefined {
    const SPAN_START_TOKEN = '[span';

    if (!translation.startsWith(SPAN_START_TOKEN, offset)) {
      return undefined;
    }

    const afterStart = offset + SPAN_START_TOKEN.length;
    const end = translation.indexOf(']', afterStart);

    if (end < 0) {
      return undefined;
    }

    if (afterStart !== end && translation.charAt(afterStart) !== ':') {
      return undefined;
    }

    const styles = afterStart !== end ? translation.substring(offset + SPAN_START_TOKEN.length + 1, end) : '';

    return {
      nextOffset: end + 1,
      token: new SpanStart(styles)
    };
  }

  private recognizeSpanEndToken(translation: string, offset: number): TokenizeResult | undefined {
    const SPAN_END_TOKEN = '[/span]';

    if (!translation.startsWith(SPAN_END_TOKEN, offset)) {
      return undefined;
    }

    return {
      nextOffset: offset + SPAN_END_TOKEN.length,
      token: SPAN_END
    };
  }

  transpile(start: number, context: TranslationMarkupTranspilerContext): TranspileResult | undefined {
    const nextToken = context.tokens[start];

    if (!(nextToken instanceof SpanStart)) {
      return undefined;
    }

    const { nextOffset, renderers } = context.transpileUntil(start + 1, token => token === SPAN_END);

    return {
      nextOffset: Math.min(nextOffset + 1, context.tokens.length),
      renderer: this.createRenderer(renderers, nextToken.styles)
    };
  }

  private createRenderer(childRenderers: TranslationMarkupRenderer[], styles: string): TranslationMarkupRenderer {
    const spanRenderer = this.rendererFactory.createElementRenderer('span', childRenderers);

    let counter = 0;

    const renderExternalLinkMarkup: (translationParameters: HashMap) => HTMLSpanElement = translationParameters => {
      const spanElement = spanRenderer(translationParameters);
      if (translationParameters['id']) {
        const baseId = translationParameters['id'];
        const uniqueId = counter === 0 ? baseId : `${baseId}-${counter}`;
        spanElement.setAttribute('id', uniqueId);
        counter++;
      }
      spanElement.setAttribute('class', translationParameters[styles]);
      return spanElement;
    };

    return renderExternalLinkMarkup;
  }
}
